import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeft, Building2, Users, Film, Settings, Plus, Trash2,
  Crown, UserCog, User, Eye, Clock, AlertTriangle, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';

const AdminWorkspaceDetail = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showPlanChange, setShowPlanChange] = useState(false);
  const [showAddOverride, setShowAddOverride] = useState(false);
  const [showRoleChange, setShowRoleChange] = useState(false);
  const [showRemoveMember, setShowRemoveMember] = useState(false);
  const [showDeleteOverride, setShowDeleteOverride] = useState(false);
  
  // Form states
  const [planChangeForm, setPlanChangeForm] = useState({ newPlan: '', reason: '', applyImmediately: false });
  const [overrideForm, setOverrideForm] = useState({ type: '', value: '', expiresAt: '', reason: '' });
  const [roleChangeForm, setRoleChangeForm] = useState({ memberId: '', newRole: '', reason: '' });
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedOverride, setSelectedOverride] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadWorkspace();
  }, [workspaceId]);

  const loadWorkspace = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_BASE}/admin/support/workspaces/${workspaceId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      
      if (!response.ok) throw new Error('Failed to load workspace');
      const data = await response.json();
      setWorkspace(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = async () => {
    if (!planChangeForm.newPlan || !planChangeForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        new_plan: planChangeForm.newPlan,
        reason: planChangeForm.reason,
        apply_immediately: planChangeForm.applyImmediately.toString()
      });
      
      const response = await fetch(
        `${API_BASE}/admin/support/workspaces/${workspaceId}/plan?${params.toString()}`,
        {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      
      if (!response.ok) throw new Error('Failed to change plan');
      
      toast.success('Plan changed successfully');
      setShowPlanChange(false);
      setPlanChangeForm({ newPlan: '', reason: '', applyImmediately: false });
      loadWorkspace();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddOverride = async () => {
    if (!overrideForm.type || !overrideForm.value || !overrideForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const body = {
        type: overrideForm.type,
        value: overrideForm.value,
        reason: overrideForm.reason,
      };
      
      if (overrideForm.expiresAt) {
        body.expires_at = new Date(overrideForm.expiresAt).toISOString();
      }
      
      const response = await fetch(
        `${API_BASE}/admin/support/workspaces/${workspaceId}/overrides`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );
      
      if (!response.ok) throw new Error('Failed to create override');
      
      toast.success('Override created successfully');
      setShowAddOverride(false);
      setOverrideForm({ type: '', value: '', expiresAt: '', reason: '' });
      loadWorkspace();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteOverride = async () => {
    if (!deleteReason) {
      toast.error('Please provide a reason');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/support/workspaces/${workspaceId}/overrides/${selectedOverride.id}?reason=${encodeURIComponent(deleteReason)}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      
      if (!response.ok) throw new Error('Failed to delete override');
      
      toast.success('Override deleted successfully');
      setShowDeleteOverride(false);
      setSelectedOverride(null);
      setDeleteReason('');
      loadWorkspace();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async () => {
    if (!roleChangeForm.newRole || !roleChangeForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        new_role: roleChangeForm.newRole,
        reason: roleChangeForm.reason,
      });
      
      const response = await fetch(
        `${API_BASE}/admin/support/workspaces/${workspaceId}/members/${selectedMember.id}/role?${params.toString()}`,
        {
          method: 'PATCH',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      
      if (!response.ok) throw new Error('Failed to change role');
      
      toast.success('Role changed successfully');
      setShowRoleChange(false);
      setSelectedMember(null);
      setRoleChangeForm({ memberId: '', newRole: '', reason: '' });
      loadWorkspace();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async () => {
    if (!deleteReason) {
      toast.error('Please provide a reason');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API_BASE}/admin/support/workspaces/${workspaceId}/members/${selectedMember.id}?reason=${encodeURIComponent(deleteReason)}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      
      if (!response.ok) throw new Error('Failed to remove member');
      
      toast.success('Member removed successfully');
      setShowRemoveMember(false);
      setSelectedMember(null);
      setDeleteReason('');
      loadWorkspace();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'admin': return <UserCog className="h-4 w-4 text-blue-500" />;
      case 'member': return <User className="h-4 w-4 text-green-500" />;
      case 'viewer': return <Eye className="h-4 w-4 text-gray-500" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  const getPlanName = (planId) => {
    switch (planId) {
      case 'plan_studio_v2':
      case 'plan_studio': return 'Studio';
      case 'plan_pro_v2': return 'Director (Pro)';
      case 'plan_pro': return 'Pro';
      default: return 'Free';
    }
  };

  const getOverrideTypeLabel = (type) => {
    switch (type) {
      case 'plan_override': return 'Plan Override';
      case 'seat_boost': return 'Seat Boost';
      case 'storage_boost': return 'Storage Boost';
      case 'feature_access': return 'Feature Unlock';
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading workspace...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/admin/support/workspaces')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">{error}</div>
      </div>
    );
  }

  if (!workspace) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/support/workspaces')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{workspace.name}</h2>
              <p className="text-sm text-muted-foreground">ID: {workspace.id}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Info & Plan */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Workspace Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Owner</span>
                <p className="font-medium">{workspace.owner_name || workspace.owner_email}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Created</span>
                <p>{new Date(workspace.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Billing Status</span>
                <p>{workspace.billing_status || 'N/A'}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Description</span>
                <p>{workspace.description || 'No description'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Plan & Billing */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Plan & Billing</CardTitle>
                <CardDescription>Current plan and usage</CardDescription>
              </div>
              <Button onClick={() => setShowPlanChange(true)}>
                Change Plan
              </Button>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-6">
                <Badge variant="default" className="text-lg px-4 py-2">
                  {workspace.plan_name}
                </Badge>
                {workspace.grace_period_end && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Grace Period until {new Date(workspace.grace_period_end).toLocaleDateString()}
                  </Badge>
                )}
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-muted p-3 rounded-lg text-center">
                  <Film className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-2xl font-bold">{workspace.active_film_count}</p>
                  <p className="text-xs text-muted-foreground">
                    / {workspace.features?.active_film_limit === -1 ? '∞' : workspace.features?.active_film_limit} Films
                  </p>
                </div>
                <div className="bg-muted p-3 rounded-lg text-center">
                  <Users className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                  <p className="text-2xl font-bold">{workspace.billable_seat_count}</p>
                  <p className="text-xs text-muted-foreground">
                    / {workspace.features?.seat_limit === -1 ? '∞' : workspace.features?.seat_limit} Seats
                  </p>
                </div>
                <div className="bg-muted p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold">{workspace.storage_used_gb || 0}</p>
                  <p className="text-xs text-muted-foreground">
                    / {workspace.features?.max_storage_gb} GB Storage
                  </p>
                </div>
                <div className="bg-muted p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold">{workspace.member_count}</p>
                  <p className="text-xs text-muted-foreground">Total Members</p>
                </div>
              </div>

              {/* Features */}
              <div className="mt-6">
                <h4 className="font-medium mb-2">Features</h4>
                <div className="flex flex-wrap gap-2">
                  {workspace.features?.collaboration && <Badge variant="secondary">Collaboration</Badge>}
                  {workspace.features?.script_upload && <Badge variant="secondary">Script Upload</Badge>}
                  {workspace.features?.can_export && <Badge variant="secondary">Export</Badge>}
                  {workspace.features?.advanced_analytics && <Badge variant="secondary">Analytics</Badge>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Members */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Members ({workspace.members?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {workspace.members?.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {getRoleIcon(member.role)}
                      <div>
                        <p className="font-medium">{member.name || member.email}</p>
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={member.is_billable ? 'default' : 'outline'}>
                        {member.role}
                      </Badge>
                      {member.role !== 'owner' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedMember(member);
                              setRoleChangeForm({ ...roleChangeForm, newRole: member.role });
                              setShowRoleChange(true);
                            }}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => {
                              setSelectedMember(member);
                              setShowRemoveMember(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Overrides */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Active Overrides
                </CardTitle>
                <CardDescription>Membership boosts and feature unlocks</CardDescription>
              </div>
              <Button size="sm" onClick={() => setShowAddOverride(true)}>
                <Plus className="h-4 w-4 mr-1" /> Add
              </Button>
            </CardHeader>
            <CardContent>
              {workspace.overrides?.length > 0 ? (
                <div className="space-y-3">
                  {workspace.overrides.map((override) => (
                    <div
                      key={override.id}
                      className="p-3 bg-muted rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="secondary">
                          {getOverrideTypeLabel(override.type)}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive h-6 w-6 p-0"
                          onClick={() => {
                            setSelectedOverride(override);
                            setShowDeleteOverride(true);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <p className="text-sm font-medium">{override.value}</p>
                      <p className="text-xs text-muted-foreground mt-1">{override.reason}</p>
                      {override.expires_at && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Expires: {new Date(override.expires_at).toLocaleDateString()}
                          {override.days_remaining !== null && ` (${override.days_remaining} days)`}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        By: {override.created_by_name || override.created_by}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm text-center py-4">
                  No active overrides
                </p>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Boosts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  setOverrideForm({ type: 'plan_override', value: 'plan_pro_v2', expiresAt: '', reason: '' });
                  setShowAddOverride(true);
                }}
              >
                <Zap className="h-4 w-4 mr-2" />
                7-day Pro Trial
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  setOverrideForm({ type: 'seat_boost', value: '3', expiresAt: '', reason: '' });
                  setShowAddOverride(true);
                }}
              >
                <Users className="h-4 w-4 mr-2" />
                +3 Seat Boost
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  setOverrideForm({ type: 'feature_access', value: '["collaboration"]', expiresAt: '', reason: '' });
                  setShowAddOverride(true);
                }}
              >
                <Settings className="h-4 w-4 mr-2" />
                Enable Collaboration
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Plan Change Dialog */}
      <Dialog open={showPlanChange} onOpenChange={setShowPlanChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Workspace Plan</DialogTitle>
            <DialogDescription>
              Change the plan for {workspace.name}. Current plan: {workspace.plan_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">New Plan</label>
              <Select
                value={planChangeForm.newPlan}
                onValueChange={(v) => setPlanChangeForm({ ...planChangeForm, newPlan: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plan_free">Free</SelectItem>
                  <SelectItem value="plan_pro_v2">Director (Pro)</SelectItem>
                  <SelectItem value="plan_studio_v2">Studio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Reason (required)</label>
              <Textarea
                value={planChangeForm.reason}
                onChange={(e) => setPlanChangeForm({ ...planChangeForm, reason: e.target.value })}
                placeholder="Enter reason for plan change..."
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="applyImmediately"
                checked={planChangeForm.applyImmediately}
                onChange={(e) => setPlanChangeForm({ ...planChangeForm, applyImmediately: e.target.checked })}
              />
              <label htmlFor="applyImmediately" className="text-sm">
                Apply restrictions immediately (no grace period)
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanChange(false)}>Cancel</Button>
            <Button onClick={handlePlanChange} disabled={submitting}>
              {submitting ? 'Changing...' : 'Change Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Override Dialog */}
      <Dialog open={showAddOverride} onOpenChange={setShowAddOverride}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Override</DialogTitle>
            <DialogDescription>
              Add a membership boost or feature unlock for {workspace.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Type</label>
              <Select
                value={overrideForm.type}
                onValueChange={(v) => setOverrideForm({ ...overrideForm, type: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select override type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plan_override">Plan Override (Temporary Upgrade)</SelectItem>
                  <SelectItem value="seat_boost">Seat Boost</SelectItem>
                  <SelectItem value="storage_boost">Storage Boost</SelectItem>
                  <SelectItem value="feature_access">Feature Unlock</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Value</label>
              {overrideForm.type === 'plan_override' ? (
                <Select
                  value={overrideForm.value}
                  onValueChange={(v) => setOverrideForm({ ...overrideForm, value: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plan_pro_v2">Director (Pro)</SelectItem>
                    <SelectItem value="plan_studio_v2">Studio</SelectItem>
                  </SelectContent>
                </Select>
              ) : overrideForm.type === 'feature_access' ? (
                <Select
                  value={overrideForm.value}
                  onValueChange={(v) => setOverrideForm({ ...overrideForm, value: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select features" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='["collaboration"]'>Collaboration</SelectItem>
                    <SelectItem value='["script_upload"]'>Script Upload</SelectItem>
                    <SelectItem value='["can_export"]'>Export</SelectItem>
                    <SelectItem value='["advanced_analytics"]'>Advanced Analytics</SelectItem>
                    <SelectItem value='["collaboration","script_upload"]'>Collaboration + Script Upload</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={overrideForm.value}
                  onChange={(e) => setOverrideForm({ ...overrideForm, value: e.target.value })}
                  placeholder={overrideForm.type === 'seat_boost' ? 'Number of extra seats' : 'Value'}
                  type={overrideForm.type === 'seat_boost' || overrideForm.type === 'storage_boost' ? 'number' : 'text'}
                />
              )}
            </div>
            <div>
              <label className="text-sm font-medium">Expires At (optional)</label>
              <Input
                type="datetime-local"
                value={overrideForm.expiresAt}
                onChange={(e) => setOverrideForm({ ...overrideForm, expiresAt: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Reason (required)</label>
              <Textarea
                value={overrideForm.reason}
                onChange={(e) => setOverrideForm({ ...overrideForm, reason: e.target.value })}
                placeholder="Enter reason for this override..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddOverride(false)}>Cancel</Button>
            <Button onClick={handleAddOverride} disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Override'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={showRoleChange} onOpenChange={setShowRoleChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Member Role</DialogTitle>
            <DialogDescription>
              Change role for {selectedMember?.name || selectedMember?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">New Role</label>
              <Select
                value={roleChangeForm.newRole}
                onValueChange={(v) => setRoleChangeForm({ ...roleChangeForm, newRole: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Reason (required)</label>
              <Textarea
                value={roleChangeForm.reason}
                onChange={(e) => setRoleChangeForm({ ...roleChangeForm, reason: e.target.value })}
                placeholder="Enter reason for role change..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleChange(false)}>Cancel</Button>
            <Button onClick={handleRoleChange} disabled={submitting}>
              {submitting ? 'Changing...' : 'Change Role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirm */}
      <AlertDialog open={showRemoveMember} onOpenChange={setShowRemoveMember}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {selectedMember?.name || selectedMember?.email} from this workspace?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Reason (required)</label>
            <Textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Enter reason for removal..."
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowRemoveMember(false); setDeleteReason(''); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={submitting || !deleteReason}
              className="bg-destructive text-destructive-foreground"
            >
              {submitting ? 'Removing...' : 'Remove Member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Override Confirm */}
      <AlertDialog open={showDeleteOverride} onOpenChange={setShowDeleteOverride}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Override</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this override? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Reason (required)</label>
            <Textarea
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder="Enter reason for deletion..."
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setShowDeleteOverride(false); setDeleteReason(''); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOverride}
              disabled={submitting || !deleteReason}
              className="bg-destructive text-destructive-foreground"
            >
              {submitting ? 'Deleting...' : 'Delete Override'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminWorkspaceDetail;
